import React from 'react'
import { Skeleton, Empty, Upload, Button, Tooltip, Popconfirm } from 'antd'
import { FilePdfOutlined, PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons'

import { SectionHeader } from '../SectionHeader'

import { ImageReview } from './ImageReview'
import { Container, AttachmentsContainer, DeletableWrapper } from '../../Styles'
import { StyledButton } from '../Button'

import isValidUrl from '../../utils/isValidURL'

import { useCaseViewContext } from '../../context'

const DeletableAttachment = ({ documentKey, onDeleteDocument, children }) => (
    <DeletableWrapper>
        {children}
        <Popconfirm
            title="Delete this attachment?"
            onConfirm={() => onDeleteDocument(documentKey)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
        >
            <Button
                className="delete-btn"
                size="small"
                icon={<DeleteOutlined style={{ color: 'white' }} />}
            />
        </Popconfirm>
    </DeletableWrapper>
);

export const Attachments = ({
    onFileUpload,
    onUploadChange,
    onBeforeUpload,
    onDeleteDocument,
    canDeleteDocuments
}) => {
    const { caseDetails } = useCaseViewContext()
    const patient = caseDetails?.patient

    const handleFileUpload = onFileUpload || (() => { });
    const handleUploadChange = onUploadChange || (() => { });
    const handleBeforeUpload = onBeforeUpload || (() => true);

    const listAdditionalDocuments = (documents = {}) => {
        let results = [];
        for (const [key, value] of Object.entries(documents)) {
            if (isValidUrl(value)) {
                const fileName = new URL(value).pathname.split('/').pop();
                const deletable = canDeleteDocuments && onDeleteDocument;

                if (!fileName.toLowerCase().endsWith('.pdf')) {
                    const image = (
                        <ImageReview
                            key={key}
                            src={value}
                            alt={fileName}
                            onClick={() => window.open(value, "_blank")}
                            onError={(e) => console.error('Image failed to load:', key, value, e)}
                            onLoad={() => console.log('Image loaded successfully:', key)}
                        />
                    );
                    results.push(deletable
                        ? <DeletableAttachment key={key} documentKey={key} onDeleteDocument={onDeleteDocument}>{image}</DeletableAttachment>
                        : image
                    );
                } else {
                    const pdf = (
                        <StyledButton
                            onClick={() => window.open(value, "_blank")}
                            key={key}
                            style={{
                                width: '150px',
                                height: '150px',
                                border: '2px solid #7BAD7E',
                                backgroundColor: '#f9f9f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FilePdfOutlined style={{ fontSize: "48px", color: "#7BAD7E" }} />
                        </StyledButton>
                    );
                    results.push(deletable
                        ? <DeletableAttachment key={key} documentKey={key} onDeleteDocument={onDeleteDocument}>{pdf}</DeletableAttachment>
                        : pdf
                    );
                }
            } else {
                console.log('Invalid URL:', key, value);
            }
        }

        results.push(
            <Upload
                key="file-upload"
                listType="picture"
                customRequest={handleFileUpload}
                onChange={handleUploadChange}
                beforeUpload={handleBeforeUpload}
                showUploadList={false}
            >
                <Tooltip title="Add Attachment" placement="right">
                    <Button
                        type="default"
                        style={{
                            width: '150px',
                            height: '150px',
                            border: '2px dashed #7BAD7E',
                            backgroundColor: '#f9f9f9'
                        }}
                        icon={<PlusCircleOutlined style={{ fontSize: "48px", color: "#7BAD7E" }} />}
                    />
                </Tooltip>
            </Upload>
        );

        return results
    }

    const hasAttachments = patient?.idProof || patient?.photo || caseDetails?.documents

    return (
        <Container>
            <SectionHeader title="Attachments" />
            <Skeleton loading={!caseDetails} active paragraph={{ rows: 3 }}>
                {!hasAttachments && <Empty description="No attachments available" />}
                <AttachmentsContainer>
                    {patient?.idProof && <ImageReview key="patient-id" src={patient.idProof} alt="patient id" />}
                    {patient?.photo && <ImageReview key="patient-photo" src={patient.photo} alt="patient photo" />}
                    {listAdditionalDocuments(caseDetails?.documents)}
                </AttachmentsContainer>
            </Skeleton>
        </Container>
    )
}
